import axios from "axios";
import { useMjmlStore } from "@/stores/mjmlStore";
import { useNotificationsStore } from "@/stores/notifications";
import {
  useSyncedTranslationsStore,
  remapTranslationUids,
} from "@/stores/syncedTranslationsStore";
import { i18nGlobal } from "../init/i18n-setup";
import { migrateApiBlock } from "@/utils/migrateApiBlocks";
import {
  buildEditSavedBlockParams,
  EditSavedBlockProps,
} from "@/utils/savedBlockEditParams";
import { ref, Ref } from "vue";
import {
  ISavedBlockApi,
  ISavedBlocksSortBy,
  ISavedBlocksPath,
  IEditParams,
} from "@/schemas/savedBlock/savedBlockApiSchema";
import useSyncedSections from "./useBlocksFromAPI/useSyncedSections";
import shortid from "shortid";
import { ISavedBlock } from "@/schemas/savedBlock/savedBlockSchema";
import { i18n } from "@/init/i18n-setup";
import { SelectPrimaryBlockLanguage } from "@/schemas/template/languageSchema";
import { api } from "@/api";
import { collectAllPages, getEmbedderHostname, PageResult } from "@topol/utils";
import {
  parseSavedBlocks,
  describeSkippedSavedBlocks,
  SkippedSavedBlock,
} from "@/utils/parseSavedBlocks";

interface GetParams {
  key: any;
  hostname: string;
  entity_id: string;
  sort_by: string;
  per_page?: number;
  current_page?: number;
  search?: string;
  desc?: boolean;
  folder_id?: number;
  type?: "saved_section" | "synced_section";
}

type ListPage = PageResult<ISavedBlockApi> & {
  parentFolderId: number | null;
  /** Invalid items dropped by parseSavedBlocks (user data, reported by name). */
  skipped: SkippedSavedBlock[];
  /** Set by fetchWholeLevel when the level was longer than the walk allows. */
  truncated?: boolean;
};
/** A whole level has no page count of its own. */
type Level = Omit<ListPage, "lastPage">;

export default () => {
  const mjmlStore = useMjmlStore();
  const notificationsStore = useNotificationsStore();
  const { t } = i18nGlobal;
  const apiUrl = api.SAVED_SECTIONS || "";

  const savedBlocks = ref<ISavedBlockApi[]>([]);
  const currentPage = ref(1);
  const pageLength = ref(25);
  const query = ref("");
  const sortingBy: Ref<ISavedBlocksSortBy> = ref("date");
  const sortDir: Ref<"asc" | "desc"> = ref("asc");
  const loading = ref(false);
  const loadingMore = ref(false);
  const actualId: Ref<number | undefined> = ref();
  const path: Ref<ISavedBlocksPath[]> = ref([]);
  const foldersInPath: Ref<ISavedBlockApi[][]> = ref([]);
  const lastPage = ref(1);
  const type = ref<"all" | "saved_section" | "synced_section">("all");
  const parentFolderId = ref<number | null>(null);

  /** The params every listing request carries; nothing from the panel state. */
  const baseParams = (): GetParams => ({
    key: mjmlStore.authorize.apiKey,
    hostname: getEmbedderHostname(),
    entity_id: mjmlStore.authorize.userId,
    sort_by: sortingBy.value,
  });

  /** Params for the panel's own listing: base + folder, direction, search, type. */
  const buildListParams = (): GetParams => {
    const params = baseParams();

    if (actualId.value !== undefined) {
      params.folder_id = actualId.value;
    }

    if (sortDir.value === "desc") {
      params.desc = true;
    }

    if (query.value !== "") {
      params.search = query.value;
    }

    if (type.value !== "all") {
      params.type = type.value;
    }

    return params;
  };

  /**
   * One GET of the listing. `null` means the request failed or the body did
   * not validate. Never notifies — the caller decides whether the user should
   * hear about it (getData does, the breadcrumb helpers stay silent).
   */
  const fetchListPage = async (params: GetParams): Promise<ListPage | null> => {
    try {
      const { data } = await axios.get(apiUrl, { params });
      if (!data || !data.success || !data.data?.data) return null;
      const parsed = parseSavedBlocks(data.data.data);
      if (!parsed) {
        console.error("Loading saved blocks failed", data.data.data);
        return null;
      }
      if (parsed.skipped.length > 0) {
        console.error("Some saved blocks are invalid", parsed.skipped);
      }
      return {
        items: parsed.blocks,
        skipped: parsed.skipped,
        lastPage: Number(data.data.lastPage) || 1,
        parentFolderId: data.data.parentFolderId || null,
      };
    } catch {
      return null;
    }
  };

  /**
   * Every item of one level: asked for without paging params (the host is
   * expected to return everything), walked page by page only when the host
   * paged anyway (see collectAllPages). `null` if any request failed.
   */
  const fetchWholeLevel = async (params: GetParams): Promise<Level | null> => {
    let parent: number | null = null;
    const skipped: SkippedSavedBlock[] = [];
    const collected = await collectAllPages<ISavedBlockApi>(async (paging) => {
      const fetched = await fetchListPage({ ...params, ...paging });
      if (fetched) parent = fetched.parentFolderId;
      if (fetched) skipped.push(...fetched.skipped);
      return fetched;
    });
    if (collected === null) return null;
    return {
      items: collected.items,
      truncated: collected.truncated,
      skipped,
      parentFolderId: parent,
    };
  };

  /** `all` = the whole current level (folder pickers); otherwise one panel page. */
  const getData = async (all?: boolean) => {
    loading.value = true;

    const result: (Level & { lastPage?: number }) | null = all
      ? await fetchWholeLevel(buildListParams())
      : await fetchListPage({
          ...buildListParams(),
          per_page: pageLength.value,
          current_page: currentPage.value,
        });

    if (result === null) {
      notificationsStore.createNotification({
        title: "Loading saved blocks failed",
        type: "error",
        text: "",
      });
    }
    if (result?.truncated) {
      notificationsStore.createNotification({
        title: t("saved_blocks_panel.listing_truncated_title"),
        type: "info",
        text: t("saved_blocks_panel.listing_truncated_text", {
          count: result.items.length,
        }),
      });
    }
    if (result && result.skipped.length > 0) {
      notificationsStore.createNotification({
        title: t("saved_blocks_panel.skipped_invalid_title"),
        type: "error",
        text: t("saved_blocks_panel.skipped_invalid_text", {
          names: describeSkippedSavedBlocks(result.skipped),
        }),
      });
    }

    lastPage.value = result?.lastPage ?? 1;
    parentFolderId.value = result?.parentFolderId ?? null;
    loading.value = false;
    await setPath();
    return result?.items ?? [];
  };

  const createSavedBlock = async (block: {
    name: ISavedBlockApi["name"];
    type: ISavedBlockApi["type"];
    definition?: ISavedBlockApi["definition"];
    translation?: ISavedBlockApi["translation"];
    index?: number;
    folder_id?: ISavedBlockApi["folder_id"];
    id?: ISavedBlockApi["id"];
    created_at?: ISavedBlockApi["created_at"];
  }) => {
    const params: IEditParams = {
      key: mjmlStore.authorize.apiKey,
      hostname: getEmbedderHostname(),
      entity_id: mjmlStore.authorize.userId,
      type: block.type,
      name: block.name,
    };
    if (actualId.value !== undefined) {
      params.folder_id = actualId.value;
    }

    if (block.type == "folder") {
      try {
        await axios.post(apiUrl, params);
      } catch {
        notificationsStore.createNotification({
          title: t("forms.folder_creation_faild"),
          type: "error",
          text: "",
        });
      }
      return;
    }

    if (!block.definition) {
      notificationsStore.createNotification({
        title: t("forms.creating_saved_block_faild"),
        type: "error",
        text: "",
      });
      return;
    }

    params.definition = block.definition;
    if (block.translation) {
      params.translation = block.translation;
    }
    try {
      const res = await axios.post(apiUrl, params);
      if (res.data.success === true) {
        return res.data.data;
      }
    } catch {
      notificationsStore.createNotification({
        title: t("forms.creating_saved_block_faild"),
        type: "error",
        text: "",
      });
    }
  };

  const deleteSavedBlocks = async (blocks: ISavedBlockApi[]) => {
    const ids: number[] = [];
    blocks.map((block) => {
      ids.push(block.id);
    });
    const params = {
      key: mjmlStore.authorize.apiKey,
      hostname: getEmbedderHostname(),
      entity_id: mjmlStore.authorize.userId,
      blocksToDelete: ids,
    };

    try {
      await axios.post(apiUrl + "/delete", params);
    } catch {
      notificationsStore.createNotification({
        title: t("forms.deleting_saved_block_faild"),
        type: "error",
        text: "",
      });
    }
  };

  const getSavedSectionMjml = async (props: {
    sync: boolean;
    id: number;
    oldUid?: string;
    definition?: ISavedBlockApi["definition"];
    translation?: ISavedBlockApi["translation"];
    syncedLang?: string;
  }) => {
    let savedBlockDefinition: ISavedBlockApi["definition"];
    let translation: ISavedBlockApi["translation"] | undefined;
    if (props.sync) {
      const { getSyncedSection } = useSyncedSections();
      const syncedSection = await getSyncedSection(props.id);
      if (!syncedSection) {
        return false;
      }
      savedBlockDefinition = syncedSection.definition;
      translation = syncedSection.translation;
    } else {
      savedBlockDefinition = props.definition;
      translation = props.translation;
    }

    if (!savedBlockDefinition) {
      return false;
    }

    let primaryTranslation: any = {};
    let primaryLanguage: string | undefined;
    let pickedLang: string | undefined;
    if (mjmlStore.mjml.langs && mjmlStore.mjml.langs.length > 0) {
      primaryLanguage = mjmlStore.mjml.langs.find((lang) => lang.primary)?.key;
    }

    if (
      translation &&
      Object.keys(translation).length > 0 &&
      (!mjmlStore.mjml.langs ||
        (mjmlStore.mjml.langs &&
          primaryLanguage &&
          !translation[primaryLanguage]))
    ) {
      const langs = Object.keys(translation);
      const title = !mjmlStore.mjml.langs
        ? i18n.global.t("layout.blockLanguageSelector.selectMutation")
        : i18n.global.t("layout.blockLanguageSelector.selectPrimaryLanguage");
      const description = !mjmlStore.mjml.langs
        ? i18n.global.t(
            "layout.blockLanguageSelector.selectMutationDescription"
          )
        : i18n.global.t(
            "layout.blockLanguageSelector.selectPrimaryLanguageDescription"
          );
      if (props.syncedLang && translation[props.syncedLang]) {
        pickedLang = props.syncedLang;
        primaryTranslation = translation[props.syncedLang];
      } else if (langs.length > 1) {
        const selectedLang = await selectPrimaryLanguage({
          langs: langs,
          title: title,
          description: description,
        });
        if (!selectedLang) return null;
        pickedLang = selectedLang;
        primaryTranslation = translation[selectedLang];
      } else {
        pickedLang = langs[0];
        primaryTranslation = translation[langs[0]];
      }
    }

    const hadNoLangsOnLoad =
      !mjmlStore.mjml.langs || mjmlStore.mjml.langs.length === 0;
    const storeSectionTranslation = (section: any, fullTranslation: any) => {
      if (!props.sync) {
        delete section.syncedLang;
        return;
      }
      if (pickedLang) {
        section.syncedLang = pickedLang;
      } else {
        delete section.syncedLang;
      }
      useSyncedTranslationsStore().setSectionTranslation(section.uid, {
        fullTranslation,
        pickedLang: hadNoLangsOnLoad ? pickedLang : undefined,
      });
    };

    if (!Array.isArray(savedBlockDefinition)) {
      const section = JSON.parse(JSON.stringify(savedBlockDefinition));
      const fullTranslation = sectionProcessing(
        section,
        primaryTranslation,
        translation
      );
      section.uid = props.oldUid ? props.oldUid : shortid.generate();
      if (props.sync) {
        section.syncedId = props.id;
      }
      storeSectionTranslation(section, fullTranslation);
      return section;
    } else {
      const sections = JSON.parse(JSON.stringify(savedBlockDefinition));
      sections.forEach((section: any) => {
        const fullTranslation = sectionProcessing(
          section,
          primaryTranslation,
          translation
        );
        section.uid = shortid.generate();
        if (props.sync) {
          section.syncedId = props.id;
        }
        storeSectionTranslation(section, fullTranslation);
      });
      return sections;
    }
  };

  function sectionProcessing(
    section: any,
    primaryTranslation: any,
    translation: ISavedBlockApi["translation"]
  ) {
    const uidMap: Record<string, string> = {};
    for (const property in mjmlStore.defaultSectionProps) {
      // eslint-disable-next-line no-prototype-builtins
      if (mjmlStore.defaultSectionProps.hasOwnProperty(property)) {
        // @ts-expect-error TODO: fix noImplicitAny error here
        section[property] = mjmlStore.defaultSectionProps[property];
      }
    }
    for (const col of section.children) {
      col.uid = shortid.generate();
      col.children = col.children.map((b: any) => migrateApiBlock(b));
      for (const block of col.children) {
        const previousUid = block.uid;
        block["uid"] = shortid.generate();
        uidMap[previousUid] = block.uid;
        const blockTranslation = primaryTranslation[previousUid];
        if (!mjmlStore.mjml.langs && blockTranslation) {
          if ("content" in blockTranslation && "content" in block) {
            block.content = blockTranslation.content;
          }
          if ("attributes" in blockTranslation) {
            for (const attribute in blockTranslation.attributes) {
              block.attributes[attribute] =
                blockTranslation.attributes[attribute];
            }
          }
          if ("children" in blockTranslation && "children" in block) {
            for (let i = 0; i < blockTranslation.children.length; i++) {
              if (
                block.children[i] &&
                blockTranslation.children[i] &&
                "attributes" in blockTranslation.children[i]
              ) {
                for (const attribute in blockTranslation.children[i]
                  .attributes) {
                  block.children[i].attributes[attribute] =
                    blockTranslation.children[i].attributes[attribute];
                }
              }
            }
          }
        }
        if (mjmlStore.mjml.langs) {
          mjmlStore.addSpecialBlockTranslations({
            block: block,
            previousUid: previousUid,
            translations: translation,
            primaryTranslation: primaryTranslation,
          });
        }
      }
    }
    return remapTranslationUids(translation, uidMap);
  }

  const addSavedBlock = async (props: {
    sync: boolean;
    definition: ISavedBlockApi["definition"];
    id: number;
    sectionIndex?: number;
    toEmpty?: boolean;
    translation?: ISavedBlockApi["translation"];
  }) => {
    if (props.sectionIndex === undefined) {
      props.sectionIndex = mjmlStore.mjml.children[0].children.length;
    }
    let sectionIndex = props.sectionIndex;

    let sections: ISavedBlock["definition"] = await getSavedSectionMjml({
      sync: props.sync,
      id: props.id,
      oldUid: undefined,
      definition: props.definition,
      translation: props.translation,
    });

    if (!sections) {
      return false;
    }
    mjmlStore.takeSnapshot();

    if (!Array.isArray(sections)) {
      sections = [sections];
    }

    sections.forEach((section, index) => {
      //if adding savedblock to empty structure, we need to replace the structure
      let removeElements = 0;
      if (props.toEmpty && index === 0) {
        removeElements = 1;
      }

      mjmlStore.mjml.children[0].children.splice(
        sectionIndex,
        removeElements,
        section
      );
      sectionIndex++;
    });
    mjmlStore.edittedWithoutSave = true;
    return true;
  };

  const editSavedBlock = async (id: number, props: EditSavedBlockProps) => {
    const params = buildEditSavedBlockParams(
      {
        entity_id: mjmlStore.authorize.userId,
        hostname: getEmbedderHostname(),
        key: mjmlStore.authorize.apiKey,
      },
      props
    );

    try {
      await axios.patch(apiUrl + "/" + id, params);
      if (props.definition) {
        const { updateSyncedSections } = useSyncedSections();
        updateSyncedSections();
      }
      return true;
    } catch {
      notificationsStore.createNotification({
        title: t("forms.renaming_saved_block_faild"),
        type: "error",
        text: "",
      });
      return false;
    }
  };

  /** One ancestor level of the breadcrumb, whole (see fetchWholeLevel). */
  const getFolderData = (folderId: number) =>
    fetchWholeLevel({ ...baseParams(), folder_id: folderId });

  const buildPathFromAPI = async (folderId: number, parentFolderId: number) => {
    let currentId: number = folderId;
    let currentParentId: number | null = parentFolderId;
    path.value = [];
    foldersInPath.value = [];
    let isFirstIteration = true;
    // The root level does not depend on the ancestor walk; fetch it alongside.
    const rootFoldersRequest = loadRootFolders();

    while (currentParentId !== null) {
      const folderData = await getFolderData(currentParentId);
      if (!folderData) break;
      if (isFirstIteration) {
        savedBlocks.value = folderData.items;
      }
      const folderName = folderData.items.find(
        (item) => item.id === currentId
      )?.name;

      foldersInPath.value.unshift(
        folderData.items.filter((item) => item.type === "folder")
      );
      path.value.unshift({
        id: currentId,
        name: folderName || "",
      });

      currentId = currentParentId;
      currentParentId = folderData.parentFolderId;
      isFirstIteration = false;
    }

    // Načteme root složky
    const rootFolders = await rootFoldersRequest;

    foldersInPath.value.unshift(
      rootFolders.filter((item) => item.type === "folder")
    );
    path.value.unshift({
      id: currentId,
      name: rootFolders.find((item) => item.id === currentId)?.name || "",
    });
  };

  /** The whole root level (breadcrumb of the folder pickers). */
  const loadRootFolders = async () =>
    (await fetchWholeLevel(baseParams()))?.items ?? [];

  async function setPath() {
    if (actualId.value !== undefined) {
      if (
        typeof parentFolderId.value === "number" &&
        path.value.findIndex((p) => p.id === parentFolderId.value) === -1
      ) {
        await buildPathFromAPI(actualId.value, parentFolderId.value);
        return;
      }

      if (
        savedBlocks.value.length === 0 &&
        typeof parentFolderId.value !== "number"
      ) {
        // Jsme v rootu ale savedBlocks je prázdný - načteme root
        path.value.length = 0;
        foldersInPath.value.length = 0;
        savedBlocks.value = await loadRootFolders();
        foldersInPath.value.push(
          savedBlocks.value.filter((item) => item.type === "folder")
        );
      }

      let folderInPath = -1;
      let isInPath = false;
      folderInPath = path.value.findIndex((item) => item.id === actualId.value);
      if (folderInPath !== -1) {
        isInPath = true;
      } else {
        foldersInPath.value.map((item, index) => {
          if (folderInPath !== -1) return;
          item.map((folder) => {
            if (folderInPath !== -1) return;
            if (folder.id === actualId.value) folderInPath = index;
          });
        });
      }
      if (folderInPath !== -1) {
        path.value.length = folderInPath + (isInPath ? 1 : 0);
        foldersInPath.value.length = folderInPath + 1;
      }
      if (!isInPath) {
        path.value.push({
          id: actualId.value,
          name:
            savedBlocks.value.find((item) => item.id === actualId.value)
              ?.name ||
            foldersInPath.value[folderInPath]?.find(
              (item) => item.id === actualId.value
            )?.name ||
            "",
        });
      }
    } else {
      path.value.length = 0;
      foldersInPath.value.length = 0;
    }
  }

  async function getBlocks(reset?: boolean) {
    if (reset) {
      query.value = "";
      actualId.value = undefined;
      currentPage.value = 1;
    }
    if (currentPage.value > 1) {
      const toPage = currentPage.value;
      currentPage.value = 1;
      savedBlocks.value = await getData();
      // Page 1 is loaded; fetch pages 2..toPage. (Running the loop toPage
      // times inflated currentPage by one on every reload and kept requesting
      // pages past lastPage.)
      for (let page = 2; page <= toPage; page++) {
        await loadMore();
      }
    } else {
      savedBlocks.value = await getData();
    }
  }

  async function loadMore() {
    loadingMore.value = true;
    currentPage.value++;

    const moreSaved = await getData();

    savedBlocks.value = savedBlocks.value.concat(moreSaved);

    loadingMore.value = false;
  }

  async function selectPrimaryLanguage(
    props: SelectPrimaryBlockLanguage
  ): Promise<string | null> {
    return new Promise((resolve) => {
      // Předáme modalu pole jazyků a callback
      window.$event.emit("selectPrimaryLanguage", {
        langs: props.langs,
        title: props.title,
        description: props.description,
        resolve, // ten zavolá modal, až bude mít výběr
      });
    });
  }

  return {
    getData,
    getBlocks,
    loadMore,
    addSavedBlock,
    createSavedBlock,
    editSavedBlock,
    deleteSavedBlocks,
    getSavedSectionMjml,
    savedBlocks,
    currentPage,
    pageLength,
    query,
    sortingBy,
    sortDir,
    loading,
    loadingMore,
    path,
    foldersInPath,
    actualId,
    lastPage,
    type,
  };
};
